import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { useApiFetch } from "../../api/client";
import { Btn } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Chip } from "../../components/ui/Chip";
import { errorMessage } from "../cockpit/format";
import { collisionText, collisionTitle, uploadVariant } from "./format";
import { uploadFacilities } from "./reviewApi";
import type { FacilityUploadResult, FacilityUploadRow } from "./types";

interface Props {
  onClose: () => void;
  onImported: () => void;
}

type Filter = "All" | "Collisions" | FacilityUploadRow["action"];

const FILTERS: readonly Filter[] = ["All", "Created", "Matched", "Collisions", "Invalid"];

function inFilter(row: FacilityUploadRow, filter: Filter): boolean {
  if (filter === "All") return true;
  if (filter === "Collisions") return row.collisions.length > 0;
  return row.action === filter;
}

function count(result: FacilityUploadResult, filter: Filter): number {
  return result.rows.filter((row) => inFilter(row, filter)).length;
}

export function FacilityUploadDialog({ onClose, onImported }: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<FacilityUploadResult | null>(null);
  const [imported, setImported] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("All");

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const run = async (chosen: File, dryRun: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const outcome = await uploadFacilities(apiFetchRef.current, chosen, dryRun);
      setResult(outcome);
      if (!dryRun) {
        setImported(true);
        onImported();
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const choose = (e: ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0] ?? null;
    setFile(chosen);
    setResult(null);
    setImported(false);
    setFilter("All");
    if (chosen) void run(chosen, true);
  };

  const changes = result ? result.created + result.matched : 0;
  const shown = result ? result.rows.filter((row) => inFilter(row, filter)) : [];

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-[rgba(12,14,10,0.65)] z-[1000] p-6"
      role="presentation"
      onClick={onClose}
    >
      <Card
        className="w-full max-w-4xl max-h-full overflow-auto"
        role="dialog"
        aria-modal="true"
        aria-label="Upload facilities"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-1">
          <h3 className="m-0 text-lg">Upload Facilities</h3>
          <Btn variant="ghost" onClick={onClose} aria-label="Close">
            Close
          </Btn>
        </div>
        <p className="mt-0 mb-4 text-[13px] text-rs-light-gray">
          Upload a CSV in the ResinSmart facility format. Facilities you add are ResinSmart Verified and marked Manual.
          Each row is checked against the registry first, so nothing is added twice. A row is a duplicate when its
          Facility ID is already on file, or when it belongs to a facility with a similar company name that has the same
          street address (house numbers a few apart count) or sits within{" "}
          {result ? `${result.duplicateMiles} mile${result.duplicateMiles === 1 ? "" : "s"}` : "a short distance"}.
          EPA's own positions are only accurate to a few hundred metres, so the distance allowed is widened for them. A
          duplicate is marked Verified instead of being added again, and EPA's coordinates are kept.
        </p>

        <label className="flex flex-col gap-1.5 text-xs text-rs-light-gray mb-4">
          CSV file
          <input
            type="file"
            accept=".csv,text/csv"
            aria-label="CSV file"
            disabled={busy || imported}
            onChange={choose}
            className="text-[13px] text-rs-off-white"
          />
        </label>

        {error && <p className="text-rs-red text-[13px] mt-0 mb-3">{error}</p>}
        {busy && (
          <p className="text-rs-light-gray text-[13px] mt-0 mb-3">{result ? "Importing…" : "Checking the file…"}</p>
        )}

        {result && (
          <>
            <p className="mt-0 mb-3 text-[13px]" role="status">
              {imported ? (
                <>
                  <strong>Imported.</strong> {result.created.toLocaleString()} added, {result.matched.toLocaleString()}{" "}
                  matched and marked Verified
                  {result.invalid > 0 ? `, ${result.invalid.toLocaleString()} could not be read` : ""}.
                </>
              ) : (
                <>
                  <strong>Preview, nothing saved yet.</strong> {result.created.toLocaleString()} will be added,{" "}
                  {result.matched.toLocaleString()} match facilities already here and will be marked Verified
                  {result.invalid > 0 ? `, ${result.invalid.toLocaleString()} cannot be read` : ""}.
                </>
              )}
            </p>

            {result.collided > 0 && (
              <div
                className="mb-3 rounded-md border border-rs-amber/45 bg-rs-amber/9 px-3 py-2 text-[13px]"
                role="alert"
              >
                <strong className="text-rs-amber">
                  {result.collided.toLocaleString()} {result.collided === 1 ? "row shares" : "rows share"} an address or
                  place with a facility that has a different name.
                </strong>{" "}
                {imported ? "They were added as separate facilities." : "They will be added as separate facilities."} A
                new name or owner at the same site is the usual cause, so check each one against the facility listed
                under it before you {imported ? "rely on" : "import"} it.{" "}
                <button
                  className="bg-transparent border-none p-0 text-rs-amber underline"
                  onClick={() => setFilter("Collisions")}
                >
                  Show collisions
                </button>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mb-3">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  className={`bg-transparent border rounded-[3px] px-2 py-0.5 text-[12px] ${
                    filter === f ? "border-rs-green text-rs-green" : "border-rs-jet-light text-rs-light-gray"
                  }`}
                  aria-pressed={filter === f}
                  onClick={() => setFilter(f)}
                >
                  {f} {count(result, f).toLocaleString()}
                </button>
              ))}
            </div>

            <div className="card p-0 overflow-auto max-h-[340px] mb-4">
              <table>
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Name</th>
                    <th>Result</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 && (
                    <tr>
                      <td colSpan={4}>No rows.</td>
                    </tr>
                  )}
                  {shown.map((row) => (
                    <tr key={row.line}>
                      <td className="mono">{row.line}</td>
                      <td>{row.name || "—"}</td>
                      <td>
                        <div className="flex flex-wrap gap-1.5">
                          <Chip variant={uploadVariant(row.action)}>{row.action}</Chip>
                          {row.collisions.length > 0 && <Chip variant="warn">Collision</Chip>}
                        </div>
                      </td>
                      <td>
                        {row.detail}
                        {row.externalFacilityId && (
                          <span className="text-rs-light-gray"> · {row.externalFacilityId}</span>
                        )}
                        {row.notes.map((note) => (
                          <div key={note} className="text-rs-light-gray text-[12px]">
                            {note}
                          </div>
                        ))}
                        {row.collisions.map((clash) => (
                          <div key={clash.registryId} className="mt-1 text-[12px] text-rs-amber">
                            <strong>{collisionTitle(clash)}:</strong> {collisionText(clash)}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-2">
              {!imported && (
                <Btn disabled={busy || changes === 0 || !file} onClick={() => file && void run(file, false)}>
                  <span className="material-symbols-sharp">upload_file</span>
                  Import {changes.toLocaleString()} {changes === 1 ? "Row" : "Rows"}
                </Btn>
              )}
              <Btn variant="ghost" onClick={onClose}>
                {imported ? "Done" : "Cancel"}
              </Btn>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
